import { useQueryClient } from '@tanstack/react-query';

import React, { useState } from 'react';

import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Box, Breadcrumbs, CircularProgress, IconButton, Link, Paper, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';

import { CodeBaseAsyncOperationModel, CodeBaseStatesModel } from '../../../api/api';
import useToastService from '../../../common/hooks/useToastService';
import LayerDeploymentPanel, { DeployableCodebaseType } from './LayerDeploymentPanel';

export type TabValue = 'domain' | 'connectors' | 'read-models' | 'authorization';

const TABS: Array<{ value: TabValue; label: string }> = [
	{ value: 'domain', label: 'Domain' },
	{ value: 'connectors', label: 'Linked Connectors' },
	{ value: 'authorization', label: 'Auth Scopes' },
	{ value: 'read-models', label: 'Read Models' },
];

export interface BreadcrumbItem {
	label: string;
	adornment?: React.ReactNode;
	href?: string;
	onClick?: () => void;
}

export interface ContextLayerTabBarProps {
	activeTab: TabValue;
	onTabChange: (tab: TabValue) => void;
	contextId: string;
	/** Optional breadcrumb trail rendered above the tab row. */
	breadcrumbs?: BreadcrumbItem[];
	domainCodebaseState?: CodeBaseStatesModel;
	applicationCodebaseState?: CodeBaseStatesModel;
	onPackageDomain: () => Promise<CodeBaseAsyncOperationModel | undefined>;
	onDeployDomain: () => Promise<CodeBaseAsyncOperationModel | undefined>;
	onPackageApplication: () => Promise<CodeBaseAsyncOperationModel | undefined>;
	onDeployApplication: () => Promise<CodeBaseAsyncOperationModel | undefined>;
	onTrackCodeOperation: (operation?: CodeBaseAsyncOperationModel) => Promise<CodeBaseAsyncOperationModel | undefined>;
	/** Called after a successful domain codebase advancement so sub-views can refetch. */
	onDomainAdvanced?: () => void;
	/** Called after a successful application codebase advancement so sub-views can refetch. */
	onApplicationAdvanced?: () => void;
	activeDeploymentLayer: DeployableCodebaseType;
	onActiveDeploymentLayerChange: (layer: DeployableCodebaseType) => void;
	onRefresh?: () => void | Promise<void>;
	refreshing?: boolean;
}

export default function ContextLayerTabBar({
	activeTab,
	onTabChange,
	contextId,
	breadcrumbs,
	domainCodebaseState,
	applicationCodebaseState,
	onPackageDomain,
	onDeployDomain,
	onPackageApplication,
	onDeployApplication,
	onTrackCodeOperation,
	onDomainAdvanced,
	onApplicationAdvanced,
	activeDeploymentLayer,
	onActiveDeploymentLayerChange,
	onRefresh,
	refreshing = false,
}: ContextLayerTabBarProps) {
	const queryClient = useQueryClient();
	const toast = useToastService();
	const [isAdvancing, setIsAdvancing] = useState(false);
	const [errorMessages, setErrorMessages] = useState<string[] | null>(null);
	const [errorTitle, setErrorTitle] = useState('');

	const activeCodebaseType = activeDeploymentLayer;

	const handleTabChange = (_: React.SyntheticEvent, v: TabValue) => {
		setErrorMessages(null);
		onTabChange(v);
	};

	const handleAdvance = async (next: CodeBaseStatesModel, current: CodeBaseStatesModel) => {
		if (!activeCodebaseType) return;
		setIsAdvancing(true);
		setErrorMessages(null);

		const pkgFn = activeCodebaseType === 'domain' ? onPackageDomain : onPackageApplication;
		const deployFn = activeCodebaseType === 'domain' ? onDeployDomain : onDeployApplication;
		const label = activeCodebaseType === 'domain' ? 'Domain logic' : 'Application logic';
		const onAdvanced = activeCodebaseType === 'domain' ? onDomainAdvanced : onApplicationAdvanced;

		try {
			let operation: CodeBaseAsyncOperationModel | undefined;
			let finalStatusVerb = '';

			if (current === CodeBaseStatesModel.Changed && next === CodeBaseStatesModel.Packaged) {
				operation = await pkgFn();
				toast.success(`${label} packaging queued`);
				finalStatusVerb = 'packaged';
			} else {
				operation = await deployFn();
				const verb = current === CodeBaseStatesModel.Deployed ? 'redeployed' : 'deployed';
				toast.success(`${label} ${verb} queued`);
				finalStatusVerb = verb;
			}

			const completedOperation = await onTrackCodeOperation(operation);
			if (isFailedOperation(completedOperation)) {
				const messages = getOperationMessages(completedOperation, `${label} operation failed.`);
				const title = `${label} operation failed`;
				setErrorMessages(messages);
				setErrorTitle(title);
				toast.error(formatOperationFailureToast(title, messages));
				return;
			}

			if (completedOperation) {
				toast.success(`${label} ${finalStatusVerb} successfully`);
			}

			queryClient.invalidateQueries({ queryKey: ['codeBase', contextId, activeCodebaseType] });
			onAdvanced?.();
		} catch {
			const messages = ['An unexpected error occurred. Check console for details.'];
			setErrorMessages(messages);
			setErrorTitle('Operation failed');
			toast.error(formatOperationFailureToast('Operation failed', messages));
		} finally {
			setIsAdvancing(false);
		}
	};

	return (
		<Paper sx={{ flexShrink: 0, overflow: 'hidden', backgroundColor: 'background.header', backgroundImage: 'none', border: 0, boxShadow: 'none' }}>
			{/* ── Single header row — four sections spaced evenly ── */}
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{
					borderBottom: '1px solid',
					borderColor: 'divider',
					pl: 1.5,
					pr: 0,
					minHeight: 56,
					bgcolor: 'background.header',
				}}
			>
				{/* 1. Breadcrumbs */}
				{breadcrumbs && breadcrumbs.length > 0 && (
					<Box
						sx={{
							minWidth: 0,
							position: 'relative',
							py: 0.25,
							'&::before': {
								content: '""',
								position: 'absolute',
								top: '105%',
								left: -12,
								right: -12,
								height: { xs: 42, sm: 54 },
								backgroundImage: 'url("/assets/randol-stream.png")',
								backgroundRepeat: 'no-repeat',
								backgroundPosition: 'left center',
								backgroundSize: '100% 100%',
								transform: 'translateY(-50%)',
								opacity: 0.92,
								pointerEvents: 'none',
								zIndex: 0,
							},
						}}
					>
						<Breadcrumbs
							maxItems={3}
							aria-label="breadcrumb"
							sx={{
								minWidth: 0,
								position: 'relative',
								zIndex: 1,
								'& .MuiBreadcrumbs-ol, & .MuiBreadcrumbs-li': { alignItems: 'center' },
								'& .MuiBreadcrumbs-separator': { mx: 0.25, display: 'flex', alignItems: 'center' },
							}}
						>
							{breadcrumbs.map((crumb, i) => {
								const isLast = i === breadcrumbs.length - 1;
								return isLast ? (
									<Typography key={i} noWrap sx={{ display: 'flex', alignItems: 'center', fontSize: '0.65rem', lineHeight: 1, color: 'text.primary', fontWeight: 600 }}>
										{crumb.label}
										{crumb.adornment && <Box component="span" sx={{ display: 'inline-flex', ml: 0.75 }}>{crumb.adornment}</Box>}
									</Typography>
								) : (
									<Link
										key={i}
										component="button"
										underline="hover"
										onClick={crumb.onClick}
										sx={{ display: 'flex', alignItems: 'center', fontSize: '0.65rem', lineHeight: 1, color: 'text.secondary', cursor: 'pointer', background: 'none', border: 'none', p: 0 }}
									>
										{crumb.label}
									</Link>
								);
							})}
						</Breadcrumbs>
					</Box>
				)}

				{/* 2. Tabs */}
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					sx={{
						minHeight: 56,
						'& .MuiTabs-indicator': { display: 'none' },
					}}
				>
					{TABS.map(({ value, label }) => (
						<Tab
							key={value}
							label={label}
							value={value}
							sx={{
								minHeight: 56,
								fontSize: '0.78rem',
								color: 'text.primary',
								borderBottom: '2px solid transparent',
								borderRadius: 1,
								'&.Mui-selected': { color: 'text.primary', fontWeight: 700, bgcolor: 'action.selected', borderBottomColor: 'primary.main' },
								'&:hover': { bgcolor: 'action.hover' },
							}}
						/>
					))}
				</Tabs>

				<Stack direction="row" alignItems="center" spacing={0.5}>
					{onRefresh && (
						<Tooltip title="Refresh context">
							<span>
								<IconButton aria-label="Refresh context" onClick={() => void onRefresh()} disabled={refreshing} size="small">
									{refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
								</IconButton>
							</span>
						</Tooltip>
					)}
					<LayerDeploymentPanel
						activeCodebaseType={activeCodebaseType}
						domainCodebaseState={domainCodebaseState}
						applicationCodebaseState={applicationCodebaseState}
						onActiveCodebaseTypeChange={onActiveDeploymentLayerChange}
						onAdvance={handleAdvance}
						loading={isAdvancing}
						operationErrors={errorMessages}
						operationErrorTitle={errorTitle}
						operationErrorSubtitle="Review the errors below and try again."
					/>
				</Stack>
			</Stack>
		</Paper>
	);
}

const isFailedOperation = (operation?: CodeBaseAsyncOperationModel) => operation?.status?.toLowerCase() === 'failed';

const getOperationMessages = (operation: CodeBaseAsyncOperationModel | undefined, fallback: string) =>
	operation?.messages?.length ? operation.messages : [fallback];

const formatOperationFailureToast = (title: string, messages: string[]) => {
	const firstMessage = messages.find((message) => message.trim().length > 0);
	return firstMessage ? `${title}: ${firstMessage}` : title;
};
